import { useState, useCallback } from 'react';
import { AttendanceController } from '../core';

const useAttendance = () => {
  const [attendanceMethods, setAttendanceMethods] = useState([]);
  const [validatingGuests, setValidatingGuests] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [generatingAttendance, setGeneratingAttendance] = useState(false);
  const [attendanceQR, setAttendanceQR] = useState(null);
  const [attendanceLink, setAttendanceLink] = useState(null);

  const [error, setError] = useState(null);

  const toggleAttendanceMethod = useCallback((method) => {
    setAttendanceMethods(prev => 
      prev.includes(method) 
        ? prev.filter(m => m !== method)
        : [...prev, method]
    );
  }, []);

  const validateAndGenerateCredentials = useCallback(async (eventId, eventDetails, userEmail, onSuccess = null) => {
    setValidatingGuests(true);
    setGeneratingAttendance(true);
    setError(null);
    setValidationResult(null);
    setAttendanceQR(null);
    setAttendanceLink(null);
    
    try {
      const result = await AttendanceController.validateAndGenerateCredentials(eventId, eventDetails, userEmail);
      
      if (result.success) {
        setValidationResult(result.data.validation);
        
        if (result.data.credentials.link) {
          setAttendanceLink(result.data.credentials.link);
        }
        
        if (result.data.credentials.qr) {
          setAttendanceQR(result.data.credentials.qr);
        }
        
        // Ejecutar callback de éxito si se proporciona
        if (onSuccess && typeof onSuccess === 'function') {
          try {
            await onSuccess();
          } catch (callbackError) {
            console.error('Error in onSuccess callback:', callbackError);
          }
        }
      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Error in validateAndGenerateCredentials:', error);
      setError(error.message || 'Error al validar invitados');
    }
    
    setValidatingGuests(false);
    setGeneratingAttendance(false);
  }, []);



  const clearAttendanceData = useCallback(() => {
    setAttendanceQR(null);
    setAttendanceLink(null);
    setValidationResult(null);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const downloadQR = useCallback(async (qrCodeImage, eventTitle) => {
    try {
      if (!qrCodeImage) {
        throw new Error('No hay código QR para descargar');
      }
      
      // Crear enlace de descarga
      const link = document.createElement('a');
      link.href = qrCodeImage;
      link.download = `QR_${eventTitle || 'Evento'}_${new Date().toISOString().split('T')[0]}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading QR:', error);
      setError('Error al descargar el código QR');
    }
  }, []);

  const copyToClipboard = useCallback(async (url) => {
    try {
      if (!url) {
        throw new Error('No hay URL para copiar');
      }
      
      await navigator.clipboard.writeText(url);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      setError('Error al copiar el enlace');
    }
  }, []);

  return {
    // State
    attendanceMethods,
    validatingGuests,
    validationResult,
    generatingAttendance,
    attendanceQR,
    attendanceLink,

    error,
    
    // Actions
    toggleAttendanceMethod,
    validateAndGenerateCredentials,
    clearAttendanceData,
    clearError,
    downloadQR,
    copyToClipboard
  };
};

export default useAttendance;